import { SettingsManager } from "@viamap/viamap2-common";
import { FilterType, FilterSpec, FilterValue } from "../states/ExploreSearchState";
import { BBOXTURF } from "./EmbeddedVectorMap";
import AWS from "aws-sdk";

/**
 * Interface to Elastic Search
 */

export type UTMGridData = UTMGridItem[];

export type UTMGridItem = {
   key: string,
   doc_count: number,
   boxCorner_easting: number,
   boxCorner_norting: number,
   centerUTM32_easting: number,
   centerUTM32_northing: number
}

export class ESInterfaceJWTAuth {
   private indexName: string = "";

   private jwtIdToken: string | null = null;
   private noSQLDataUrl: string = "";
   private static refreshTokenPromise: Promise<string | undefined> | null = null;

   constructor(index: string, userSession?: any) {
      this.indexName = index;
      this.noSQLDataUrl = SettingsManager.getSystemSetting("getNoSQLDataUrl");

      this.jwtIdToken = userSession?.idToken?.getJwtToken();
   }


   runtimeMapping = {};

   private getAuthStorageContext(): {
      authData: { ClientId: string; Region?: string };
      idTokenKey: string;
      accessTokenKey: string;
      refreshTokenKey: string;
   } | undefined {
      try {
         const authData = SettingsManager.getSystemSetting("authData") as { ClientId?: string; Region?: string } | undefined;
         if (!authData || !authData.ClientId) {
            return undefined;
         }

         const lastAuthUserKey = `CognitoIdentityServiceProvider.${authData.ClientId}.LastAuthUser`;
         const username = localStorage.getItem(lastAuthUserKey);

         if (!username) {
            return undefined;
         }

         return {
            authData: { ClientId: authData.ClientId, Region: authData.Region },
            idTokenKey: `CognitoIdentityServiceProvider.${authData.ClientId}.${username}.idToken`,
            accessTokenKey: `CognitoIdentityServiceProvider.${authData.ClientId}.${username}.accessToken`,
            refreshTokenKey: `CognitoIdentityServiceProvider.${authData.ClientId}.${username}.refreshToken`
         };
      } catch (error) {
         console.warn("Failed to read auth storage context:", error);
         return undefined;
      }
   }

   private getJwtTokenFromStorage(): string | undefined {
      const context = this.getAuthStorageContext();
      if (!context) return undefined;
      try {
         return localStorage.getItem(context.idTokenKey) || undefined;
      } catch (error) {
         console.warn("Failed to get JWT token from localStorage:", error);
         return undefined;
      }
   }

   private decodeJwtPayload(token: string): { exp?: number } | undefined {
      try {
         const payload = token.split(".")[1];
         if (!payload) return undefined;
         const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
         const paddedPayload = normalizedPayload.padEnd(normalizedPayload.length + ((4 - normalizedPayload.length % 4) % 4), "=");
         const decoded = JSON.parse(atob(paddedPayload));
         return decoded;
      } catch {
         return undefined;
      }
   }

   private isTokenExpired(token: string, skewMs: number = 30 * 1000): boolean {
      const payload = this.decodeJwtPayload(token);
      if (!payload?.exp) {
         return false;
      }
      const expiresAtMs = payload.exp * 1000;
      return Date.now() >= (expiresAtMs - skewMs);
   }

   private async refreshJwtToken(): Promise<string | undefined> {
      const context = this.getAuthStorageContext();
      if (!context?.authData.Region) {
         return undefined;
      }

      let refreshToken: string | null = null;
      try {
         refreshToken = localStorage.getItem(context.refreshTokenKey);
      } catch {
         refreshToken = null;
      }
      if (!refreshToken) {
         return undefined;
      }

      if (!ESInterfaceJWTAuth.refreshTokenPromise) {
         ESInterfaceJWTAuth.refreshTokenPromise = new Promise((resolve) => {
            const cognito = new AWS.CognitoIdentityServiceProvider({ region: context.authData.Region });
            cognito.initiateAuth(
               {
                  AuthFlow: "REFRESH_TOKEN_AUTH",
                  ClientId: context.authData.ClientId,
                  AuthParameters: {
                     REFRESH_TOKEN: refreshToken || ""
                  }
               } as AWS.CognitoIdentityServiceProvider.InitiateAuthRequest,
               (error, result) => {
                  if (error) {
                     console.warn("Failed to refresh JWT token:", error);
                     resolve(undefined);
                     return;
                  }

                  const idToken = result?.AuthenticationResult?.IdToken;
                  const accessToken = result?.AuthenticationResult?.AccessToken;
                  if (!idToken) {
                     resolve(undefined);
                     return;
                  }

                  try {
                     localStorage.setItem(context.idTokenKey, idToken);
                     if (accessToken) {
                        localStorage.setItem(context.accessTokenKey, accessToken);
                     }
                  } catch {
                     // Ignore storage write errors and still use the refreshed token in-memory.
                  }
                  resolve(idToken);
               }
            );
         });
      }

      try {
         const refreshedToken = await ESInterfaceJWTAuth.refreshTokenPromise;
         if (refreshedToken) {
            this.jwtIdToken = refreshedToken;
         }
         return refreshedToken;
      } finally {
         ESInterfaceJWTAuth.refreshTokenPromise = null;
      }
   }

   private async getJwtToken(forceRefresh: boolean = false): Promise<string | undefined> {
      const fromStorage = this.getJwtTokenFromStorage();
      const token = this.jwtIdToken || fromStorage;
      if (token && !forceRefresh && !this.isTokenExpired(token)) {
         this.jwtIdToken = token;
         return token;
      }

      const refreshedToken = await this.refreshJwtToken();
      if (refreshedToken) {
         return refreshedToken;
      }

      if (token) {
         this.jwtIdToken = token;
      }
      return token || undefined;
   }

   private async postNoSql(requestBody: any, jwtToken?: string): Promise<Response> {
      return fetch(this.noSQLDataUrl, {
         method: "POST",
         headers: {
            ...(jwtToken && { "Authorization": `Bearer ${jwtToken}` }),
            "content-type": "application/json"
         },
         body: JSON.stringify(requestBody)
      });
   }

   private async postNoSqlWithRefreshRetry(requestBody: any): Promise<Response> {
      const token = await this.getJwtToken();
      let response = await this.postNoSql(requestBody, token);

      if (response.status === 401 || response.status === 403) {
         const refreshedToken = await this.getJwtToken(true);
         response = await this.postNoSql(requestBody, refreshedToken);
      }
      return response;
   }

   async doCount(selectableFilterList: { [id: string]: FilterSpec },
      activeFilters: { [id: string]: FilterValue }): Promise<number> {

      const requestBody = {
         index: "/" + this.indexName + "/_count",
         query: this.makeQuery(selectableFilterList, activeFilters)
      }
      const response = await this.postNoSqlWithRefreshRetry(requestBody);
      if (response.status != 200) {
         throw new Error(response.statusText);
      }
      let body = await response.json();
      let countRes = body.count;
      let msg = `There were ${countRes} matches`;
      console.info(msg);

      return countRes;
   }


   async doQuery(query: any, pageSize?: number): Promise<any> {
      // If pageSize is provided, use pagination
      if (pageSize !== undefined) {
         return this.doQueryWithPagination(query, pageSize);
      }

      const requestBody = {
         index: "/" + this.indexName + "/_search",
         query: query
      };

      try {
         const response = await this.postNoSqlWithRefreshRetry(requestBody);
         if (response.status != 200) {
            throw new Error(response.statusText);
         }
         let body = await response.json();
         let result = body;
         return result;
      } catch (err: any) {
         console.error("Got error searching: ", JSON.stringify(query), err);
         throw new Error(err.message || err);
      }
   }

   private async doQueryWithPagination(query: any, pageSize: number): Promise<any> {
      const allHits: any[] = [];
      let currentPage = 0;
      let total: number | null = null;
      let searchAfter: any[] | null = null;
      let hasMore = true;
      const maxPages = 10000; // Safety limit

      // Ensure query has sort for search_after
      const queryWithSort = query.sort
         ? { ...query }
         : { ...query, sort: [{ _id: 'asc' }] };

      while (hasMore && currentPage < maxPages) {
         currentPage++;

         try {
            const esQuery: any = {
               ...queryWithSort,
               size: pageSize
            };

            if (searchAfter) {
               esQuery.search_after = searchAfter;
            }

            const requestBody = {
               index: "/" + this.indexName + "/_search",
               query: esQuery,
               maxRows: pageSize,
               ...(searchAfter && { searchAfter: searchAfter })
            };

            const jwtToken = await this.getJwtToken();

            const response = await fetch(this.noSQLDataUrl, {
               method: "POST",
               headers: {
                  "content-type": "application/json",
                  ...(jwtToken && { "Authorization": `Bearer ${jwtToken}` })
               },
               body: JSON.stringify(requestBody)
            });

            if (response.status !== 200) {
               const errorText = await response.text();
               throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const body = await response.json();
            const pagination = body.pagination;
            const hits = body.hits?.hits || [];
            allHits.push(...hits);

            // Store total from first response
            if (currentPage === 1) {
               const totalObj = body.hits?.total;
               total = typeof totalObj === 'object' ? totalObj.value : totalObj;
            } else if (pagination) {
               total = pagination.total;
            }

            // Update pagination state
            if (pagination) {
               hasMore = pagination.hasMore;
               searchAfter = pagination.nextSearchAfter;
            } else {
               // Fallback: check if we got fewer results than requested
               hasMore = hits.length === pageSize;
               if (hasMore && hits.length > 0) {
                  const lastHit = hits[hits.length - 1];
                  if (lastHit.sort) {
                     searchAfter = lastHit.sort;
                  } else if (lastHit._id) {
                     searchAfter = [lastHit._id];
                  } else {
                     hasMore = false;
                  }
               }
            }

            // Safety check: if we got no results, we're done
            if (hits.length === 0) {
               hasMore = false;
            }

         } catch (error: any) {
            throw new Error(`Failed to fetch page ${currentPage}: ${error.message || error}`);
         }
      }

      // Return in same format as non-paginated query
      return {
         hits: {
            total: { value: total || allHits.length },
            hits: allHits
         }
      };
   }


   async doSearch(selectableFilterList: { [id: string]: FilterSpec },
      activeFilters: { [id: string]: FilterValue },
      count: number,
      includeTotalAndReturned: true,
      pageSize?: number): Promise<{ data: any, total: number, returned: number }>
   async doSearch(selectableFilterList: { [id: string]: FilterSpec },
      activeFilters: { [id: string]: FilterValue },
      count?: number,
      includeTotalAndReturned?: false,
      pageSize?: number): Promise<any[]>
   async doSearch(selectableFilterList: { [id: string]: FilterSpec },
      activeFilters: { [id: string]: FilterValue },
      count?: number,
      includeTotalAndReturned?: boolean,
      pageSize?: number): Promise<any> {
      let query = this.makeQuery(selectableFilterList, activeFilters);
      if (count) {
         query = { ...query, "size": count, "version": true };
      }
      query = { ...this.runtimeMapping, ...query };

      // If pageSize is provided, use pagination
      if (pageSize !== undefined) {
         return this.doSearchWithPagination(query, pageSize, includeTotalAndReturned, count);
      }

      const requestBody = {
         index: "/" + this.indexName + "/_search",
         query: query
      };

      try {
         const response = await this.postNoSqlWithRefreshRetry(requestBody);
         if (response.status != 200) {
            throw new Error(response.statusText);
         }
         let body = await response.json();
         let result = body.hits.hits.map((hit: any) => { return { _version: hit._version, ...hit._source } });
         if (includeTotalAndReturned) {
            return { data: result, total: body.hits.total.value, returned: body.hits.hits.length };
         }
         return result;
      } catch (err: any) {
         console.error("Got error searching: ", JSON.stringify(query), err);
         throw new Error(err.message || err);
      }
   }

   private async doSearchWithPagination(query: any, pageSize: number, includeTotalAndReturned?: boolean, requestedSize?: number): Promise<any> {
      const allHits: any[] = [];
      let currentPage = 0;
      let total: number | null = null;
      let searchAfter: any[] | null = null;
      let hasMore = true;
      const maxPages = 10000; // Safety limit

      // Ensure query has sort for search_after (use _id as default if no sort specified)
      const queryWithSort = query.sort
         ? { ...query }
         : { ...query, sort: [{ _id: 'asc' }] };

      while (hasMore && currentPage < maxPages && (!requestedSize || allHits.length < requestedSize)) {
         currentPage++;

         try {
            const currentPageSize = requestedSize
               ? Math.min(pageSize, requestedSize - allHits.length)
               : pageSize;
            const esQuery: any = {
               ...queryWithSort,
               size: currentPageSize
            };

            if (searchAfter) {
               esQuery.search_after = searchAfter;
            }

            const requestBody = {
               index: "/" + this.indexName + "/_search",
               query: esQuery,
               maxRows: currentPageSize,
               ...(searchAfter && { searchAfter: searchAfter })
            };

            const jwtToken = await this.getJwtToken();

            const response = await fetch(this.noSQLDataUrl, {
               method: "POST",
               headers: {
                  "content-type": "application/json",
                  ...(jwtToken && { "Authorization": `Bearer ${jwtToken}` })
               },
               body: JSON.stringify(requestBody)
            });

            if (response.status !== 200) {
               const errorText = await response.text();
               throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const body = await response.json();
            const pagination = body.pagination;
            const hits = body.hits?.hits || [];
            allHits.push(...hits);

            // Store total from first response
            if (currentPage === 1) {
               const totalObj = body.hits?.total;
               total = typeof totalObj === 'object' ? totalObj.value : totalObj;
            } else if (pagination) {
               total = pagination.total;
            }

            // Update pagination state
            if (pagination) {
               hasMore = pagination.hasMore && (!requestedSize || allHits.length < requestedSize);
               searchAfter = pagination.nextSearchAfter;
            } else {
               // Fallback: check if we got fewer results than requested
               hasMore = hits.length === currentPageSize && (!requestedSize || allHits.length < requestedSize);
               if (hasMore && hits.length > 0) {
                  const lastHit = hits[hits.length - 1];
                  if (lastHit.sort) {
                     searchAfter = lastHit.sort;
                  } else if (lastHit._id) {
                     searchAfter = [lastHit._id];
                  } else {
                     hasMore = false;
                  }
               }
            }

            // Safety check: if we got no results, we're done
            if (hits.length === 0) {
               hasMore = false;
            }

         } catch (error: any) {
            throw new Error(`Failed to fetch page ${currentPage}: ${error.message || error}`);
         }
      }

      // Transform hits to match expected format
      const result = allHits.map((hit: any) => { return { _version: hit._version, ...hit._source } });

      if (includeTotalAndReturned) {
         return { data: result, total: total || result.length, returned: result.length };
      }
      return result;
   }

   async doGetGroups(selectableFilterList: { [id: string]: FilterSpec },
      activeFilters: { [id: string]: FilterValue }): Promise<any> {
      let query = this.makeQuery(selectableFilterList, activeFilters);
      query = {
         ...this.runtimeMapping,
         ...query,
         "size": 0, // don't return data for hits
         "aggs": {
            "group_by_10km": {
               "terms": {
                  "field": "bygningspunkt_DDKNcelle10km.keyword",
                  "size": 10000 // return all groups
               }
            }
         }
      }

      const requestBody = {
         index: "/" + this.indexName + "/_search",
         query: query
      };

      try {
         const response = await this.postNoSqlWithRefreshRetry(requestBody);
         if (response.status != 200) {
            throw new Error(response.statusText);
         }
         let body = await response.json();
         let result = body.aggregations.group_by_10km.buckets.map((bkt: any) => { return this.transformBucket(bkt, 10000); }) || undefined;

         return result;
      } catch (err: any) {
         console.error("Got error searching: ", JSON.stringify(query), err);
         throw new Error(err.message || err);
      }
   }

   makeQuery(selectableFilterList: { [id: string]: FilterSpec },
      activeFilters: { [id: string]: FilterValue }): {} | undefined {
      let filters = Object.keys(activeFilters).map((key) => {
         return {
            item: key,
            value: activeFilters[key]
         }
      });
      let filterTerms = filters.reduce<{}[]>((prev, curr) => {
         let { item, value } = curr;
         let filterSpec = selectableFilterList[item];
         if (filterSpec.index_field_override) {
            item = filterSpec.index_field_override;
         }
         let terms = this.makeFilterTerm(item, filterSpec.filterType, value);
         if (terms) {

         }
         return [...prev, ...terms];
      }, []);
      let body = filters.length > 0 ? {
         "query": {
            "bool": {
               "filter": filterTerms
            }
         }
      } : undefined;

      return body;
   }

   private makeFilterTerm(item: string, filterType: FilterType, value: FilterValue, subField?: string): {}[] {
      switch (filterType) {
         case FilterType.IntegerRange:
         case FilterType.DateRange:
            let fltr = {};
            if (value && value[0] && value[0] !== undefined) {
               fltr = { ...fltr, "gte": value[0] };
            }
            if (value && value[1] && value[1] !== undefined) {
               fltr = { ...fltr, "lte": value[1] };
            }
            return ([{
               "range": {
                  [item]: fltr
               }
            }]);
         case FilterType.BoundingBox: {
            if (value && value.length > 3) {
               let bbox: BBOXTURF = value as BBOXTURF;
               return ([{
                  "geo_bounding_box": {
                     "geopoint": {
                        "top_left": {
                           "lat": bbox[3],
                           "lon": bbox[0]
                        },
                        "bottom_right": {
                           "lat": bbox[1],
                           "lon": bbox[2]
                        }
                     }
                  }
               }]);
            } else {
               return [];
            }
         }
         case FilterType.PolygonFromLayer:
         case FilterType.Polygon: {
            if (value && Array.isArray(value[0]?.[0]?.[0])) {
               return ([{
                  "bool": {
                     "should": [{
                        "geo_shape": {
                           "geopoint": {
                              "shape": {
                                 "type": "MultiPolygon",
                                 "coordinates": value
                              },
                              "relation": "within"
                           }
                        },
                     }, {
                        "geo_shape": {
                           "geoshape": {
                              "shape": {
                                 "type": "MultiPolygon",
                                 "coordinates": value
                              },
                              "relation": "intersects"
                           }
                        }
                     }
                     ]
                  }
               }]);
            }
            if (value && value.length > 0) {
               return ([{
                  "bool": {
                     "should": [{
                        "geo_shape": {
                           "geopoint": {
                              "shape": {
                                 "type": "polygon",
                                 "coordinates": value
                              },
                              "relation": "within"
                           }
                        }
                     }, {
                        "geo_shape": {
                           "geoshape": {
                              "shape": {
                                 "type": "polygon",
                                 "coordinates": value
                              },
                              "relation": "intersects"
                           }
                        }
                     }]
                  }
               }]);
            } else {
               return [];
            }
         }
         case FilterType.StaticValue:
         case FilterType.Integer:
         case FilterType.SingleSelect:
         case FilterType.Select:
            if (value && value.length > 1) {
               return ([{
                  terms: {
                     [item]: value
                  }
               }])
            }
            if (value && value[0]) {
               return ([{
                  term: {
                     [item]: value && value[0] || ""
                  }
               }]);
            }
            return []
         case FilterType.SelectTextList:
            if (value && value.length > 1) {
               return [{
                  bool: {
                     should: value.map((v) => ({
                        match: {
                           [item]: v
                        }
                     }))
                  }
               }];
            }
            if (value && value[0]) {
               return ([{
                  match: {
                     [item]: value && value[0] || ""
                  }
               }]);
            }
         case FilterType.SelectText:
            if (value && value.length > 1) {
               // multiselect
               return ([{
                  terms: {
                     [item + ".keyword"]: value
                  }
               }]);
            }
            if (value && value[0]) {
               // singleselect
               return ([{
                  match: {
                     [item]: value && value[0] || ""
                  }
               }]);
            }
            return []
         case FilterType.String:
            if (value && value[0]) {
               let parts = value[0].split(" ");
               return parts.map((p) => {
                  return {
                     match: {
                        [item]: p
                     }
                  }
               });
            }
            return ([])
         case FilterType.StringInNestedObjectList:
            if (value && value.length > 0) {
               let parts = value[0].split(" ");
               return value.map((_v) => ({
                  nested: {
                     path: item,
                     query: {
                        bool: {
                           filter: parts.map((p) => {
                              return {
                                 match: {
                                    [subField ? `${item}.${subField}` : item]: p
                                 }
                              }
                           })
                        }
                     }
                  }
               }));
            }
            return ([])
         case FilterType.IntegerSet:
            if (value && value.length > 0) {
               if (value.length > 65536) {
                  throw new Error(`Maximum 65,536 terms in a request has been exceeded`);
               }
               return ([
                  {
                     terms: {
                        [item]: value
                     }
                  }
               ])
            }

            return ([])

         default:
            throw new Error("Unexpected filterType:" + filterType);
      }
   }

   async createDocument(id: string, body: any): Promise<any> {
      return this.runCommand("POST", this.indexName + "/_create/" + id, body);
   }

   async updateDocument(id: string, body: any): Promise<any> {
      delete body._version; // remove _version from the body it cannot be updated
      return this.runCommand("POST", this.indexName + "/_doc/" + id, body);
   }

   async getDocument(id: string): Promise<any> {
      return this.runCommand("GET", this.indexName + "/_doc/" + id, undefined);
   }

   async deleteDocument(id: string): Promise<any> {
      return this.runCommand("DELETE", this.indexName + "/_doc/" + id, undefined);
   }

   async indexExists(indexName: string): Promise<boolean> {
      try {
         // Try to get mapping - if it succeeds, index exists
         await this.runCommand("GET", indexName + "/_mapping", undefined);
         return true;
      } catch (error: any) {
         // If error is 404 or index_not_found, index doesn't exist
         if (error.message?.includes("404") ||
            error.message?.includes("not found") ||
            error.message?.includes("index_not_found_exception")) {
            return false;
         }
         // For other errors, assume index doesn't exist
         return false;
      }
   }

   async createIndex(indexName: string, options = {}) {
      return this.runCommand("PUT", indexName, options);
   }

   async setMetadata(indexName: string, metadata: object) {
      return this.runCommand("PUT", indexName + "/_mapping", {
         _meta: metadata
      });
   }

   async getMetadata(indexName: string): Promise<any> {
      return new Promise((resolve, reject) => {
         this.runCommand("GET", indexName + "/_mapping", undefined)
            .then((res) => {
               resolve(res[indexName].mappings._meta);
            })
            .catch((error) => {
               reject(error.message || error);
            })
      });
   }

   async emptyTheIndex(indexName: string) {
      return this.runCommand("POST", indexName + "/_delete_by_query", {
         "query": {
            "match_all": {}
         }
      });
   }

   async getItemCount(indexName: string, options = {}) {
      return this.runCommand("POST", indexName + "/_count", options);
   }

   async bulk(indexName: string, data: any) {
      return this.runCommand("POST", indexName + "/_bulk", data, true);
   }


   async runCommand(method: string, urlCommand: string, body: any, noJSON = false): Promise<any> {
      const requestBody: any = {
         index: "/" + urlCommand,
         method: method
      };

      if (body !== undefined) {
         if (noJSON) {
            // For bulk operations, body is already a string - send as-is
            requestBody.body = body;
         } else {
            // For document operations, stringify the body so lambda can handle it
            requestBody.body = typeof body === 'string' ? body : JSON.stringify(body);
         }
      }

      try {
         const response = await this.postNoSqlWithRefreshRetry(requestBody);
         if (response.status != 200) {
            const errorText = await response.text().catch(() => response.statusText);
            throw new Error(`HTTP ${response.status}: ${errorText}`);
         }
         let result = await response.json();
         if (result.error) {
            throw new Error(result.error.reason || result.error);
         }
         return result;
      } catch (err: any) {
         console.error("Got error in runCommand: ", urlCommand, err);
         throw new Error(err.message || err);
      }
   }


   private transformBucket(bkt: any, boxSizeMeters: number): UTMGridItem {
      let elms = bkt.key.split("_");
      let norting = elms.length >= 3 ? elms[1] * boxSizeMeters : 0;
      let easting = elms.length >= 3 ? elms[2] * boxSizeMeters : 0;
      return {
         key: bkt.key,
         doc_count: bkt.doc_count,
         boxCorner_norting: norting,
         boxCorner_easting: easting,
         centerUTM32_northing: norting && norting + boxSizeMeters / 2,
         centerUTM32_easting: easting && easting + boxSizeMeters / 2
      }
   }

}